import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IconChevronDown, IconChevronLeft } from "./icons";
import { Plus, Flame } from "lucide-react";
import PremiumCard from "./PremiumCard";
import { slurpApi } from "../../lib/slurpApi";

/**
 * LaunchForm — one question at a time. Click Continue to walk through the flow;
 * the final screen is a big preview of the token card with a LAUNCH button under it.
 *
 * State lives in the parent (DeployToken.js); we pass setters down as props.
 */

const SUPPLY_STEPS = [
  { label: "1M", value: 1_000_000 },
  { label: "10M", value: 10_000_000 },
  { label: "100M", value: 100_000_000 },
  { label: "1B", value: 1_000_000_000 },
  { label: "10B", value: 10_000_000_000 },
  { label: "100B", value: 100_000_000_000 },
  { label: "1T", value: 1_000_000_000_000 },
];

const compact = (n) => {
  const v = +n;
  if (!Number.isFinite(v) || v <= 0) return "—";
  const trim = (x) => String(x).replace(/\.?0+$/, "");
  if (v >= 1e12) return `${trim((v / 1e12).toFixed(2))}T`;
  if (v >= 1e9)  return `${trim((v / 1e9).toFixed(2))}B`;
  if (v >= 1e6)  return `${trim((v / 1e6).toFixed(2))}M`;
  if (v >= 1e3)  return `${trim((v / 1e3).toFixed(1))}K`;
  return `${v}`;
};

export default function LaunchForm(p) {
  const navigate = useNavigate();

  // Stepper state
  const [step, setStep] = useState(0);
  const stageRef = useRef(null);

  // Local knobs.
  const [startingMcapUsd, setStartingMcapUsd] = useState("3000");
  const [supplyIdx, setSupplyIdx] = useState(3);
  const [devBuyOn, setDevBuyOn] = useState(false);
  // Fee handling — "claim" sends SOL fees to the creator; "burn" auto-swaps
  // every claim into the token and burns it. UI surfaces a flame badge for
  // burn-mode tokens so traders can see the creator's pledge at a glance.
  const [feeMode, setFeeMode] = useState("claim");
  const [notifyFollowers, setNotifyFollowers] = useState(true);

  // Anti-vamp ticker check — populated when the user tries to advance past
  // the name step with a banned or recently-used ticker. Reset whenever
  // the ticker changes (the useEffect lives below the destructure since
  // it reads `tokenSymbol` from props).
  const [tickerError, setTickerError] = useState(null); // { reason, minutesAgo?, name? }
  const [checkingTicker, setCheckingTicker] = useState(false);

  const {
    isExecuting,
    signer,
    tokenName,
    setTokenName,
    tokenSymbol,
    setTokenSymbol,
    description,
    setDescription,
    selectedFile,
    previewUrl,
    imageUrl,
    imageError,
    isUploading,
    uploadProgress,
    handleFileChange,
    tokenSupply,
    setTokenSupply,
    deploymentFee,
    updateDeploymentFee,
    website,
    setWebsite,
    twitter,
    setTwitter,
    telegram,
    setTelegram,
    selectedNetwork,
    getMaxDeploymentFee,
    solUsd,
    deployToken,
    txHash,
    txResult,
  } = p;

  const syncSupplyFromSlider = (i) => {
    setSupplyIdx(i);
    setTokenSupply?.(String(SUPPLY_STEPS[i].value));
  };

  const maxDevBuy = getMaxDeploymentFee?.(selectedNetwork) ?? 1;
  const minDevBuy = 0.01;

  // Clear any banned/vamp warning when the user edits the ticker or
  // name so they see a fresh state on retry.
  useEffect(() => { setTickerError(null); }, [tokenSymbol, tokenName]);

  const canAdvance = useCallback(
    (idx) => {
      switch (idx) {
        case 0:
          return !!(tokenName?.trim() && tokenSymbol?.trim());
        case 1:
          // Image is mandatory — must be uploaded (imageUrl set), not
          // mid-upload, and not in an error state.
          return !!imageUrl && !isUploading && !imageError;
        case 2:
          return !!(tokenSupply && Number(tokenSupply) > 0);
        case 3:
          return Number(startingMcapUsd) > 0;
        case 4:
          if (!devBuyOn) return true;
          const v = Number(deploymentFee);
          return v >= minDevBuy && v <= maxDevBuy;
        case 5:
          return true; // socials optional
        default:
          return true;
      }
    },
    [tokenName, tokenSymbol, tokenSupply, startingMcapUsd, devBuyOn, deploymentFee, maxDevBuy, imageUrl, isUploading, imageError]
  );

  const advance = useCallback(async () => {
    // Step 0 = name + ticker. Run anti-vamp + reserved-ticker check
    // server-side before letting the user move on. All other steps
    // are pure local state.
    if (step === 0) {
      const sym = (tokenSymbol || "").trim();
      const nm = (tokenName || "").trim();
      if (sym || nm) {
        setCheckingTicker(true);
        try {
          const res = await slurpApi.checkSymbol(sym, nm);
          if (res?.allowed === false) {
            setTickerError({
              reason: res.reason,
              minutesAgo: res.minutes_ago,
              name: res.name,
            });
            return;
          }
        } catch {
          /* network blip — let the user proceed; backend re-validates on launch */
        } finally {
          setCheckingTicker(false);
        }
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }, [step, tokenSymbol]);
  const back = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  useEffect(() => {
    // Mobile users benefit from the auto-scroll because the next question
    // is below the fold after they tap Continue — without it they'd be
    // confused where the new field went. Desktop users have everything
    // visible in one viewport already; the scroll just yanks the page
    // around for no reason. So: only scroll on narrow viewports.
    if (stageRef.current && window.matchMedia("(max-width: 768px)").matches) {
      stageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const canLaunch =
    !isExecuting &&
    signer &&
    tokenName?.trim() &&
    tokenSymbol?.trim() &&
    tokenSupply &&
    Number(startingMcapUsd) > 0 &&
    (!devBuyOn ||
      (Number(deploymentFee) >= minDevBuy && Number(deploymentFee) <= maxDevBuy));

  const previewToken = useMemo(
    () => ({
      _demo: true,
      name: tokenName || "Your token",
      symbol: (tokenSymbol || "TKR").toUpperCase(),
      contractAddress: "0x".padEnd(42, "0"),
      chain: "Solana",
      image: previewUrl ? { url: previewUrl } : null,
      market_cap_usd: Number(startingMcapUsd) > 0 ? Number(startingMcapUsd) : null,
      volume_usd_24h: 0,
      price_change_24h: null,
      holders: 1,
      age_seconds: 0,
      website,
      twitter,
      telegram,
    }),
    [tokenName, tokenSymbol, startingMcapUsd, previewUrl, website, twitter, telegram]
  );

  // ------------------------------------------------------------------ //
  // Step definitions — one conversational question per step
  // ------------------------------------------------------------------ //

  const STEPS = [
    {
      key: "name",
      ask: "What's your token called?",
      body: (
        <div className="pm-q__body">
          <div className="pm-q__row">
            <div className="pm-q__field">
              <label className="pm-q__label">Name</label>
              <input
                className="pm-q__input"
                maxLength={32}
                autoFocus
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder="Cortex"
              />
            </div>
            <div className="pm-q__field">
              <label className="pm-q__label">Ticker</label>
              <input
                className="pm-q__input pm-q__input--mono"
                maxLength={10}
                value={tokenSymbol}
                onChange={(e) => setTokenSymbol(e.target.value.toUpperCase())}
                placeholder="CTX"
              />
            </div>
          </div>
          {tickerError && (
            <div className="pm-q__warning" role="alert">
              {tickerError.reason === "banned" ? (
                <><b>Don't be cheeky now,</b> pick something different you little rascal.</>
              ) : (
                <>
                  <b>Hold up — be original!</b> ${tokenSymbol} just launched
                  {tickerError.minutesAgo === 0
                    ? " moments ago"
                    : ` ${tickerError.minutesAgo} min ago`}
                  {tickerError.name ? ` as "${tickerError.name}"` : ""}.
                  Pick something different and let theirs cook for an hour.
                </>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "vibe",
      ask: "Give it a vibe.",
      hint: "Image is required. One-liner is optional.",
      body: (
        <div className="pm-q__body">
          <div className="pm-q__upload" onClick={() => document.getElementById("pm-file-upload").click()}>
            <div className="pm-q__upload-preview">
              {previewUrl && !imageError ? <img src={previewUrl} alt="preview" /> : null}
            </div>
            <div className="pm-q__upload-text">
              <div className="pm-q__upload-title">{selectedFile?.name || "Upload image"}</div>
              <div className="pm-q__upload-sub">
                {isUploading ? `Uploading ${uploadProgress}%` : imageError || "PNG · JPG · GIF · WEBP · up to 2 MB"}
              </div>
            </div>
            <input
              id="pm-file-upload"
              type="file"
              onChange={handleFileChange}
              accept="image/png, image/jpeg, image/gif, image/webp"
              style={{ display: "none" }}
            />
          </div>
          <div className="pm-q__field">
            <label className="pm-q__label">
              One-liner
              <span className="pm-q__counter">{description.length}/50</span>
            </label>
            <input
              className="pm-q__input"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 50))}
              maxLength={50}
              placeholder="What is this, in one breath?"
            />
          </div>
        </div>
      ),
    },
    {
      key: "supply",
      ask: "How many tokens should exist?",
      hint: "Drag the slider or type an exact number. Fixed forever once you launch.",
      body: (
        <div className="pm-q__body">
          <div className="pm-q__supply">
            <input
              className="pm-slider"
              type="range"
              min={0}
              max={SUPPLY_STEPS.length - 1}
              step={1}
              value={supplyIdx}
              onChange={(e) => syncSupplyFromSlider(Number(e.target.value))}
            />
            <div className="pm-slider-scale">
              {SUPPLY_STEPS.map((s, i, arr) => (
                <span key={s.label} style={{ left: `${(i / (arr.length - 1)) * 100}%` }}>
                  {s.label}
                </span>
              ))}
            </div>
          </div>
          <div className="pm-q__supply-value">
            {compact(tokenSupply || SUPPLY_STEPS[supplyIdx].value)} <span>{tokenSymbol || ""}</span>
          </div>
          <input
            className="pm-q__input pm-q__input--mono"
            inputMode="numeric"
            value={tokenSupply || ""}
            onChange={(e) => setTokenSupply(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="Exact amount"
          />
        </div>
      ),
    },
    {
      key: "mcap",
      ask: "What's the starting market cap?",
      hint: "Pick anywhere between $3K and $15K. Drag the slider, step 1K.",
      body: (
        <div className="pm-q__body">
          <div className="pm-q__mcap-value">
            <span className="pm-q__mcap-prefix">$</span>
            <span className="pm-q__mcap-amount">
              {Number(startingMcapUsd || 3000).toLocaleString()}
            </span>
            <span className="pm-q__mcap-unit">USD</span>
          </div>
          <input
            className="pm-slider"
            type="range"
            min={3000}
            max={15000}
            step={1000}
            value={Number(startingMcapUsd) || 3000}
            onChange={(e) => setStartingMcapUsd(String(e.target.value))}
          />
          <div className="pm-slider-scale">
            {[3, 5, 7, 9, 11, 13, 15].map((k, i, arr) => (
              <span key={k} style={{ left: `${(i / (arr.length - 1)) * 100}%` }}>
                ${k}K
              </span>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: "devbuy",
      ask: "Want to ape in yourself?",
      hint: "We can bundle your buy into the same transaction. Nothing can front-run you.",
      body: (
        <div className="pm-q__body">
          <div className="pm-q__toggle-row">
            <button
              type="button"
              className="pm-q__choice"
              data-active={!devBuyOn ? "true" : "false"}
              onClick={() => setDevBuyOn(false)}
            >
              <span className="pm-q__choice-title">Skip it</span>
              <span className="pm-q__choice-sub">Launch with LP only.</span>
            </button>
            <button
              type="button"
              className="pm-q__choice"
              data-active={devBuyOn ? "true" : "false"}
              onClick={() => setDevBuyOn(true)}
            >
              <span className="pm-q__choice-title">Yes, bag me</span>
              <span className="pm-q__choice-sub">Buy in at the starting price.</span>
            </button>
          </div>

          {devBuyOn && (
            <>
              <div className="pm-q__big-input">
                <input
                  className="pm-q__big-input-field"
                  inputMode="decimal"
                  value={deploymentFee || ""}
                  onChange={(e) => updateDeploymentFee(e.target.value)}
                  placeholder="0.00"
                />
                <span className="pm-q__big-input-unit">
                  {selectedNetwork === "SOLANA" ? "SOL" : selectedNetwork === "BSC" ? "BNB" : "ETH"}
                </span>
              </div>
              {(() => {
                const sol = Number(deploymentFee);
                if (!Number.isFinite(sol) || sol <= 0) return null;
                const usd = solUsd && sol > 0 ? sol * solUsd : null;
                const mcap = Number(startingMcapUsd);
                const supply = Number(tokenSupply) || SUPPLY_STEPS[supplyIdx].value;
                const approxTokens =
                  usd && mcap > 0 && supply > 0 ? (usd / mcap) * supply : null;
                return (
                  <div className="pm-q__review">
                    <Row
                      label="Spending"
                      value={usd ? `${sol} SOL · ~$${usd.toFixed(2)}` : `${sol} SOL`}
                    />
                    <Row
                      label="Est. tokens"
                      value={
                        approxTokens
                          ? `~${compact(approxTokens)} ${tokenSymbol || ""}`
                          : "—"
                      }
                    />
                  </div>
                );
              })()}
            </>
          )}
        </div>
      ),
    },
    {
      key: "socials",
      ask: "Any links worth sharing?",
      hint: "All optional. Shown on your token's card and page.",
      body: (
        <div className="pm-q__body">
          <div className="pm-q__field">
            <label className="pm-q__label">Website</label>
            <div className="pm-q__prefix">
              <span className="pm-q__prefix-label">https://</span>
              <input
                className="pm-q__prefix-input"
                type="text"
                value={website.replace(/^https?:\/\//i, "")}
                onChange={(e) =>
                  setWebsite(e.target.value ? `https://${e.target.value.replace(/^https?:\/\//i, "")}` : "")
                }
                placeholder="fries.food"
              />
            </div>
          </div>
          <div className="pm-q__row">
            <div className="pm-q__field">
              <label className="pm-q__label">X</label>
              <div className="pm-q__prefix">
                <span className="pm-q__prefix-label">x.com/</span>
                <input
                  className="pm-q__prefix-input"
                  type="text"
                  value={twitter.replace(/^https?:\/\/(x|twitter)\.com\//i, "")}
                  onChange={(e) => {
                    const handle = e.target.value.replace(/^@/, "").replace(/^https?:\/\/(x|twitter)\.com\//i, "");
                    setTwitter(handle ? `https://x.com/${handle}` : "");
                  }}
                  placeholder="handle"
                />
              </div>
            </div>
            <div className="pm-q__field">
              <label className="pm-q__label">Telegram</label>
              <div className="pm-q__prefix">
                <span className="pm-q__prefix-label">t.me/</span>
                <input
                  className="pm-q__prefix-input"
                  type="text"
                  value={telegram.replace(/^https?:\/\/t\.me\//i, "")}
                  onChange={(e) => {
                    const handle = e.target.value.replace(/^https?:\/\/t\.me\//i, "");
                    setTelegram(handle ? `https://t.me/${handle}` : "");
                  }}
                  placeholder="group"
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "feeMode",
      ask: "How do you wanna handle the fees?",
      hint: "Swap fees go half to the platform, half to you. You decide what to do with your half.",
      body: (
        <div className="pm-q__body">
          <div className="pm-q__toggle-row">
            <button
              type="button"
              className="pm-q__choice"
              data-active={feeMode === "claim" ? "true" : "false"}
              onClick={() => setFeeMode("claim")}
            >
              <span className="pm-q__choice-title">Gimme my fees!</span>
              <span className="pm-q__choice-sub">You claim your SOL whenever you want.</span>
            </button>
            <button
              type="button"
              className="pm-q__choice pm-q__choice--burn"
              data-active={feeMode === "burn" ? "true" : "false"}
              onClick={() => setFeeMode("burn")}
            >
              <span className="pm-q__choice-title">
                <Flame className="pm-q__choice-flame" />
                Let 'em burn baby!
              </span>
              <span className="pm-q__choice-sub">Fees get swapped into your token + burned forever.</span>
            </button>
            <button
              type="button"
              className="pm-q__choice pm-q__choice--lp"
              data-active={feeMode === "lp_compound" ? "true" : "false"}
              onClick={() => setFeeMode("lp_compound")}
            >
              <span className="pm-q__choice-title">Send 'em to the LP!</span>
              <span className="pm-q__choice-sub">Fees auto-compound back into the pool. Liquidity grows forever, you earn nothing in SOL.</span>
            </button>
          </div>
        </div>
      ),
    },
    {
      key: "review",
      ask: "Ready?",
      hint: "This is what your token will look like. One button, one transaction.",
      isFinal: true,
      body: (
        <div className="pm-q__body pm-q__review">
          <div className="pm-q__preview">
            <PremiumCard token={previewToken} onQuickBuy={() => {}} onCopy={() => {}} clickable={false} />
          </div>
          <label className="pm-q__notify">
            <input
              type="checkbox"
              checked={notifyFollowers}
              onChange={(e) => setNotifyFollowers(e.target.checked)}
            />
            <span className="pm-q__notify-box" aria-hidden="true" />
            <span className="pm-q__notify-text">
              <b>Notify my followers</b>
              <span className="pm-q__notify-sub">
                Send a notification to everyone following you the moment your token goes live.
              </span>
            </span>
          </label>
          <button
            className="pm-cta"
            onClick={() =>
              deployToken?.({
                startingMcapUsd: Number(startingMcapUsd),
                devBuyOn,
                devBuyAmount: devBuyOn ? Number(deploymentFee) : 0,
                feeMode,
                notifyFollowers,
              })
            }
            disabled={!canLaunch}
          >
            {isExecuting ? <>Launching…</> : <><Plus /> Launch</>}
          </button>
        </div>
      ),
    },
  ];

  const current = STEPS[step];
  const isFinal = !!current.isFinal;
  const total = STEPS.length;

  return (
    <div className="pm-launch pm-launch--flow">
      <div className="pm-launch__topbar">
        <button className="pm-launch__backhome" onClick={() => navigate("/")}>
          <IconChevronLeft /> Dashboard
        </button>
        <div className="pm-launch__progress">
          <span className="pm-launch__progress-count">
            {String(step + 1).padStart(2, "0")} <span>/</span> {String(total).padStart(2, "0")}
          </span>
          <div className="pm-launch__progress-track">
            <div
              className="pm-launch__progress-bar"
              style={{ width: `${((step + 1) / total) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="pm-q" ref={stageRef} key={current.key}>
        <div className="pm-q__ask">{current.ask}</div>
        {current.hint && <div className="pm-q__hint">{current.hint}</div>}

        {current.body}

        {!isFinal && (
          <div className="pm-q__nav">
            {step > 0 && (
              <button type="button" className="pm-q__back" onClick={back}>
                <IconChevronLeft /> Back
              </button>
            )}
            <button
              type="button"
              className="pm-q__continue"
              onClick={advance}
              disabled={!canAdvance(step) || checkingTicker}
            >
              {checkingTicker && step === 0 ? "Checking…" : "Continue"}
              <IconChevronDown />
            </button>
          </div>
        )}
        {isFinal && step > 0 && (
          <button type="button" className="pm-q__back pm-q__back--center" onClick={back}>
            <IconChevronLeft /> Change something
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="pm-q__summary-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
